/**
 * Excel context reader.
 * All Office.js READ operations are centralized here.
 * No other file in the addin may call Excel.run() for reads.
 */

import type { ExcelContext } from '@excel-ai-studio/shared';

/* global Excel, Office */

/** Result of reading the current selection with metadata */
export interface SelectionInfo {
  /** 2D array of cell values */
  readonly values: unknown[][];
  /** A1-style range address (e.g. "Sheet1!A1:C10") */
  readonly address: string;
  /** Number of rows in the selection */
  readonly rowCount: number;
  /** Number of columns in the selection */
  readonly colCount: number;
}

/** Result of reading the active cell's formula */
export interface ActiveFormulaInfo {
  /** The formula string in English/international names (=SUM), or null */
  readonly formula: string | null;
  /** The formula string in the user's locale (=SOMME for fr), or null */
  readonly formulaLocal: string | null;
  /** The cell address (e.g. "Sheet1!A1") */
  readonly cellAddress: string;
  /** The computed value of the cell */
  readonly cellValue: unknown;
}

/**
 * Read the formula of the currently active cell.
 * Returns structured info:
 *   - formula: string if the cell contains a formula, null otherwise
 *   - cellAddress: the A1-style address
 *   - cellValue: the displayed value
 *
 * Throws a user-facing Error (in French) if Office.js is unavailable
 * or the read fails.
 */
export async function readActiveFormula(): Promise<ActiveFormulaInfo> {
  try {
    return await Excel.run(async (ctx) => {
      const range = ctx.workbook.getSelectedRange();
      const cell = range.getCell(0, 0);
      cell.load(['address', 'values', 'formulas', 'formulasLocal']);
      await ctx.sync();

      const rawFormula = (cell.formulas as unknown[][])[0]?.[0];
      const rawFormulaLocal = (cell.formulasLocal as unknown[][])[0]?.[0];
      const rawValue = (cell.values as unknown[][])[0]?.[0];

      const formula =
        typeof rawFormula === 'string' && rawFormula.startsWith('=')
          ? rawFormula
          : null;

      const formulaLocal =
        typeof rawFormulaLocal === 'string' && rawFormulaLocal.startsWith('=')
          ? rawFormulaLocal
          : null;

      return {
        formula,
        formulaLocal,
        cellAddress: cell.address,
        cellValue: rawValue,
      };
    });
  } catch (err) {
    throw toContextError(err, 'lire la formule de la cellule active');
  }
}

/**
 * Read the full current Excel context.
 */
export async function readExcelContext(): Promise<ExcelContext> {
  try {
    return await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      const range = ctx.workbook.getSelectedRange();
      const activeCell = range.getCell(0, 0);

      sheet.load('name');
      range.load(['address', 'values', 'formulas']);
      activeCell.load(['address', 'values', 'formulas']);

      await ctx.sync();

      const values = range.values as unknown[][];
      const rawHeaders =
        values.length > 0
          ? (values[0] as unknown[]).map((v) => String(v ?? ''))
          : undefined;
      const headers = rawHeaders?.some((h) => h.trim() !== '') ? rawHeaders : undefined;

      const cellFormula = (activeCell.formulas as unknown[][])[0]?.[0];
      const cellValue = (activeCell.values as unknown[][])[0]?.[0];

      return {
        sheetName: sheet.name,
        selectedRange: range.address,
        selectedValues: values,
        headers,
        activeCell: activeCell.address,
        activeCellValue: cellValue,
        activeCellFormula:
          typeof cellFormula === 'string' && cellFormula.startsWith('=')
            ? cellFormula
            : undefined,
      };
    });
  } catch (err) {
    throw toContextError(err, 'lire le contexte Excel');
  }
}

/**
 * Read values from the current selection.
 */
export async function readSelectedValues(): Promise<unknown[][]> {
  try {
    return await Excel.run(async (ctx) => {
      const range = ctx.workbook.getSelectedRange();
      range.load('values');
      await ctx.sync();
      return range.values as unknown[][];
    });
  } catch (err) {
    throw toContextError(err, 'lire la selection');
  }
}

/**
 * Read values + metadata from the current selection.
 * Returns the values, address, row count and column count.
 */
export async function readSelectionInfo(): Promise<SelectionInfo> {
  try {
    return await Excel.run(async (ctx) => {
      const range = ctx.workbook.getSelectedRange();
      range.load(['values', 'address', 'rowCount', 'columnCount']);
      await ctx.sync();

      return {
        values: range.values as unknown[][],
        address: range.address,
        rowCount: range.rowCount,
        colCount: range.columnCount,
      };
    });
  } catch (err) {
    throw toContextError(err, 'lire la selection');
  }
}

/**
 * Lecture pour le dashboard : si la sélection recoupe un tableau Excel (ListObject),
 * utilise la vraie ligne d'en-têtes du tableau (ExcelApi 1.12+) pour éviter les noms
 * génériques "Colonne N" et les décalages de la 1re ligne de données.
 */
export async function readDashboardSelection(): Promise<SelectionInfo> {
  try {
    return await Excel.run(async (ctx) => {
      const range = ctx.workbook.getSelectedRange();
      range.load(['values', 'address', 'rowCount', 'columnCount', 'columnIndex', 'rowIndex']);
      await ctx.sync();

      const address = range.address;
      const rowCount = range.rowCount;
      const colCount = range.columnCount;
      let values = range.values as unknown[][];

      const canUseTables =
        typeof Office !== 'undefined' &&
        Boolean(Office.context?.requirements?.isSetSupported?.('ExcelApi', '1.12'));

      if (canUseTables) {
        try {
          const tables = range.getTables();
          tables.load('items');
          await ctx.sync();

          if (tables.items.length > 0) {
            const table = tables.items[0];
            const headerRange = table.getHeaderRowRange();
            headerRange.load(['values', 'columnIndex', 'rowIndex', 'columnCount']);
            range.load(['columnIndex', 'rowIndex']);
            await ctx.sync();

            const fullHeaderRow = ((headerRange.values as unknown[][])[0] ?? []).map((v) => String(v ?? '').trim());
            const offset = range.columnIndex - headerRange.columnIndex;
            const width = range.columnCount;

            const headers: string[] = [];
            for (let c = 0; c < width; c += 1) {
              const idx = offset + c;
              const h = idx >= 0 && idx < fullHeaderRow.length ? fullHeaderRow[idx] : '';
              headers.push(h || `Colonne ${c + 1}`);
            }

            const headerRowIdx = headerRange.rowIndex;
            const rawRows = range.values as unknown[][];
            const dataRows: unknown[][] = [];
            for (let r = 0; r < rawRows.length; r += 1) {
              if (range.rowIndex + r > headerRowIdx) {
                dataRows.push(rawRows[r]);
              }
            }

            values = [headers, ...dataRows];
          }
        } catch {
          // Conserve les valeurs brutes de la plage
        }
      }

      return {
        values,
        address,
        rowCount,
        colCount,
      };
    });
  } catch (err) {
    throw toContextError(err, 'lire la selection pour le dashboard');
  }
}

/**
 * Read values from a specific range address.
 */
export async function readRange(address: string): Promise<unknown[][]> {
  try {
    return await Excel.run(async (ctx) => {
      const sheet = ctx.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange(address);
      range.load('values');
      await ctx.sync();
      return range.values as unknown[][];
    });
  } catch (err) {
    throw toContextError(err, 'lire la plage');
  }
}

// ─── Error translation ───────────────────────────────────

interface OfficeError {
  code: string;
  message: string;
}

function isOfficeError(err: unknown): err is OfficeError {
  return (
    typeof err === 'object' &&
    err !== null &&
    typeof (err as OfficeError).code === 'string' &&
    typeof (err as OfficeError).message === 'string'
  );
}

function toContextError(err: unknown, action: string): Error {
  if (err instanceof Error && !(err as { code?: string }).code) {
    return err;
  }
  if (isOfficeError(err)) {
    return new Error(`Erreur Excel lors de ${action} (code: ${err.code}).`);
  }
  if (err instanceof Error) {
    return new Error(`Erreur inattendue lors de ${action}: ${err.message}`);
  }
  return new Error(`Erreur inattendue lors de ${action}.`);
}
